import React, { useEffect, useRef, useState } from 'react';
import { definirGestores } from '../../../api/portalCotacoes/portalCotacoesApi';
import { getUsers, User } from '../../../api/usersApi';
import { GestorCompras } from '../../../interfaces/PortalCotacoes';
import { notify } from '../../../shared/feedback/notify';
// Overlay compartilhado dos modais do sistema (definido no AddCarModal).
import '../../../components/modal/add-car/AddCarModal.css';
// Receita visual do modal de configuração de Cotações — REUSO POR IMPORT, nunca cópia
// (mesmo princípio do `ModalFiltro.css`, que serve 5 modais). O que é exclusivo daqui
// (busca de candidatos e resumo da confirmação) vive no CSS ao lado.
import './FornecedorDetalhesModal.css';
import './GerenciarGestoresModal.css';
import '../../../shared/feedback/erro-de-tela.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

/**
 * Cadastro de gestores de compras (12/08/2026 — resolve o `BUG-001` do Portal).
 *
 * Antes desta entrega a aba só LISTAVA: `PUT /gestores` existia no backend e nenhuma tela
 * o chamava, então a única forma de nomear um gestor era Swagger ou seed. Como item
 * escolhido acima do menor preço exige aprovação de gestor e **ADMIN não tem passe livre**,
 * lista vazia — o estado de qualquer instalação nova — travava cotações sem saída pela UI.
 *
 * Decisões que este componente carrega:
 *
 * - **Candidatos são só papéis de gestão** (decisão do Victor, 12/08/2026): a busca no SOAC
 *   filtra os papéis que aprovam gasto acima do menor preço — oferecer mecânico e recepção
 *   convidaria a nomeação por engano. ⚠️ Em 15/08/2026 a lista foi **reduzida a
 *   `COMPRA,ADMIN`** na correção do `BUG-V03`: ver a nota em `PAPEIS_CANDIDATOS`.
 * - **A lista de candidatos vem do SOAC** (`GET /users`, ADMIN/TI), porque o Portal não tem
 *   cadastro de usuários próprio (ADR 0014: o JWT é o mesmo). O nome vai junto no PUT,
 *   desnormalizado, e é o que a fila de aprovação exibe.
 * - **Aplicação em bloco**: adicionar e remover mexem só no estado local; nada sai até
 *   "Salvar". Combina com o PUT ser declarativo — a tela monta a lista final e envia uma vez.
 * - **Confirmação DENTRO do modal, sem segundo overlay** (passo `confirmacao`): um
 *   `ConfirmacaoModal` por cima seria irmão deste no `document`, e o clique nele contaria
 *   como "clicou fora" aqui — a mesma armadilha que o `ComoFunciona` criou no
 *   `ChecklistModal` (at-58). Dois passos no mesmo card fazem o caso não existir.
 *
 * ⚠️ O componente é montado condicionalmente pela tela (`{aberto && <Modal/>}`) e recebe a
 * lista aplicada por prop: é o par de regras que faz o rascunho de um "Cancelar" nunca
 * sobreviver para a próxima abertura (at-61/at-65).
 */

interface GerenciarGestoresModalProps {
  /** A lista APLICADA hoje — origem do estado inicial a cada abertura. */
  gestoresAtuais: GestorCompras[];
  onFechar: () => void;
  /** Recebe a lista que o backend devolveu, para a tela não precisar reconsultar. */
  onSalvo: (gestores: GestorCompras[]) => void;
  sidebarRef?: React.RefObject<HTMLDivElement>;
}

/**
 * Papéis que podem ser nomeados gestor de compras.
 *
 * ⚠️ **Reduzido de `COMPRA,ADMIN,ADMINISTRACAO,CHEFE_DE_OFICINA` para `COMPRA,ADMIN` na
 * correção do `BUG-V03`** (15/08/2026, decisão do Victor). Os dois papéis retirados **não
 * têm acesso ao Portal**: sem o módulo no menu não há sino nem badge (a notificação de
 * aprovação pendente era gravada e ficava invisível), e a fila é barrada pelo `RoleGuard`
 * do `App.tsx` e pelo `@Roles` do backend. Nomear um deles produzia um gestor que **não
 * conseguia aprovar nada pela interface** — e, como a lista não ficava vazia, a guarda
 * `SEM_GESTOR_CADASTRADO` não disparava: a cesta fechava e a cotação travava em
 * "Aguardando aprovação", o mesmo beco sem saída que a at-06 fechou por outro caminho.
 *
 * A alternativa considerada era a oposta — dar acesso ao Portal a quem fosse gestor —, e
 * foi descartada por expor o módulo inteiro a papéis operacionais só para viabilizar uma
 * aprovação.
 *
 * Continua sendo filtro de CANDIDATOS, não de permissão: quem aprova é quem está na lista
 * final. A imposição de papel vive no `@Roles` das rotas de decisão do Portal.
 */
const PAPEIS_CANDIDATOS = 'COMPRA,ADMIN';

/** Candidatos por busca: a lista é para escolher, não para navegar. */
const LIMITE_CANDIDATOS = 20;

const GerenciarGestoresModal: React.FC<GerenciarGestoresModalProps> = ({
  gestoresAtuais,
  onFechar,
  onSalvo,
  sidebarRef,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  // Estado inicial vem da prop e só daqui: a montagem condicional garante que isto rode a
  // cada abertura.
  const [selecionados, setSelecionados] = useState<GestorCompras[]>(gestoresAtuais);
  const [passo, setPasso] = useState<'edicao' | 'confirmacao'>('edicao');

  const [busca, setBusca] = useState('');
  const [candidatos, setCandidatos] = useState<User[]>([]);
  const [carregandoCandidatos, setCarregandoCandidatos] = useState(true);
  const [erroCandidatos, setErroCandidatos] = useState(false);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    const cliqueFora = (evento: MouseEvent) => {
      const alvo = evento.target as Node;
      const foraDoModal = modalRef.current && !modalRef.current.contains(alvo);
      const foraDaSidebar = !sidebarRef?.current || !sidebarRef.current.contains(alvo);
      // Salvando: fechar no meio da gravação deixaria o usuário sem saber o desfecho.
      if (foraDoModal && foraDaSidebar && !salvando) onFechar();
    };

    document.addEventListener('mousedown', cliqueFora);
    return () => document.removeEventListener('mousedown', cliqueFora);
  }, [onFechar, sidebarRef, salvando]);

  /*
   * Busca de candidatos com debounce: cada tecla dispararia uma consulta ao SOAC, e a
   * resposta fora de ordem faria a lista piscar entre dois resultados.
   */
  useEffect(() => {
    let ativo = true;
    const termo = busca.trim();

    const timer = setTimeout(async () => {
      setCarregandoCandidatos(true);
      try {
        const r = await getUsers({
          page: 1,
          limit: LIMITE_CANDIDATOS,
          status: 'ATIVOS',
          userTypes: PAPEIS_CANDIDATOS,
          ...(termo ? { search: termo } : {}),
        });
        if (!ativo) return;
        setCandidatos(r.users);
        setErroCandidatos(false);
      } catch (error) {
        if (!ativo) return;
        setCandidatos([]);
        setErroCandidatos(true);
        // Erro de API não pode virar "nenhum usuário encontrado" (at-60): a faixa no corpo
        // do modal diz que a consulta falhou, e o toast dá o motivo.
        notify.errorFrom(
          error,
          'Não foi possível carregar os usuários. Tente novamente; se persistir, acione a TI.',
        );
      } finally {
        if (ativo) setCarregandoCandidatos(false);
      }
    }, busca ? 400 : 0);

    return () => {
      ativo = false;
      clearTimeout(timer);
    };
  }, [busca]);

  const idsSelecionados = new Set(selecionados.map((g) => g.userId));
  const idsAtuais = new Set(gestoresAtuais.map((g) => g.userId));

  const adicionados = selecionados.filter((g) => !idsAtuais.has(g.userId));
  const removidos = gestoresAtuais.filter((g) => !idsSelecionados.has(g.userId));
  const temMudanca = adicionados.length > 0 || removidos.length > 0;

  function adicionar(usuario: User) {
    if (idsSelecionados.has(usuario.id)) return;
    setSelecionados((atual) => [...atual, { userId: usuario.id, nome: usuario.fullName }]);
  }

  function remover(userId: string) {
    setSelecionados((atual) => atual.filter((g) => g.userId !== userId));
  }

  async function salvar() {
    setSalvando(true);
    try {
      const lista = await definirGestores(
        selecionados.map((g) => ({ userId: g.userId, nome: g.nome ?? undefined })),
      );
      // Texto à mão: "lista de gestores" é feminino e `actionSuccess` conjuga sempre no
      // masculino singular ("Lista de gestores atualizado") — at-65, BUG-V51.
      notify.success('Lista de gestores atualizada. Vale para as próximas aprovações.');
      onSalvo(lista);
      onFechar();
    } catch (error) {
      // Erro mantém o modal aberto com o rascunho: refazer a seleção do zero seria punir
      // o usuário por uma falha que não é dele. E nada de toast de sucesso aqui.
      notify.actionError(error, { action: 'update', subject: 'a lista de gestores' });
      setPasso('edicao');
    } finally {
      setSalvando(false);
    }
  }

  return (
    <div className="add-car-modal-overlay">
      <div className="fornecedor-modal-content" ref={modalRef}>
        <div className="fornecedor-modal-header">
          <div className="fornecedor-modal-header-info">
            <h2>Gestores de compras</h2>
            <span className="fornecedor-modal-subtitulo">
              Quem pode aprovar item escolhido acima do menor preço
            </span>
          </div>
          <button
            className="close-button"
            onClick={onFechar}
            disabled={salvando}
            aria-label="Fechar modal"
          >
            &times;
          </button>
        </div>

        <div className="fornecedor-modal-body">
          {passo === 'edicao' ? (
            <>
              <div className="fornecedor-modal-secao">
                <h3>Gestores</h3>
                <p className="fornecedor-modal-hint">
                  A lista abaixo é a que vale ao salvar — quem sair dela deixa de aprovar. Administradores{' '}
                  <strong>não têm passe livre</strong>: para aprovar, precisam estar aqui como qualquer outro.
                </p>

                {selecionados.length === 0 ? (
                  <p className="gestores-aviso-vazio">
                    Sem nenhum gestor, cotação com item acima do menor preço fica parada em "Aguardando
                    aprovação" e ninguém consegue destravá-la.
                  </p>
                ) : (
                  <div className="fornecedor-contatos-lista">
                    {selecionados.map((g) => (
                      <div key={g.userId} className="fornecedor-contato-linha">
                        <div className="fornecedor-contato-dados">
                          <span className="fornecedor-contato-nome">{g.nome ?? 'Nome não disponível'}</span>
                          <span className="fornecedor-contato-canais">{g.userId}</span>
                        </div>
                        <button
                          type="button"
                          className="fornecedor-modal-btn fornecedor-modal-btn--perigo"
                          onClick={() => remover(g.userId)}
                        >
                          Remover
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              <div className="fornecedor-modal-secao">
                <h3>Adicionar gestor</h3>
                <p className="fornecedor-modal-hint">
                  A busca oferece usuários ativos dos papéis que fazem gestão de compra (Compras,
                  Administração, Chefe de Oficina e Administrador). O nome vem do cadastro de usuários do
                  sistema.
                </p>

                <input
                  className="fornecedor-modal-input"
                  value={busca}
                  onChange={(e) => setBusca(e.target.value)}
                  placeholder="Buscar por nome ou nome de usuário..."
                  aria-label="Buscar usuário"
                />

                {erroCandidatos ? (
                  /*
                    Faixa em vez de "nenhum usuário encontrado": a conclusão de que não há
                    candidato só cabe quando a consulta responde (at-60).
                  */
                  <p className="erro-de-tela" style={{ marginTop: 12 }}>
                    Não foi possível consultar os usuários do sistema, então não é possível saber quem pode ser
                    adicionado. Tente novamente; se persistir, acione a TI.
                  </p>
                ) : carregandoCandidatos ? (
                  <p className="fornecedor-modal-vazio" style={{ marginTop: 12 }}>
                    Buscando usuários...
                  </p>
                ) : candidatos.length === 0 ? (
                  <p className="fornecedor-modal-vazio" style={{ marginTop: 12 }}>
                    Nenhum usuário encontrado com esses papéis.
                  </p>
                ) : (
                  <div className="gestores-candidatos">
                    {candidatos.map((u) => {
                      const jaEsta = idsSelecionados.has(u.id);
                      return (
                        <button
                          key={u.id}
                          type="button"
                          className="gestores-candidato"
                          disabled={jaEsta}
                          onClick={() => adicionar(u)}
                        >
                          <span className="gestores-candidato-nome">{u.fullName || u.username}</span>
                          <span className="gestores-candidato-meta">
                            {u.userType}
                            {jaEsta ? ' · já é gestor' : ''}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="fornecedor-modal-secao">
              <h3>Confirmar mudança</h3>
              <p className="fornecedor-modal-hint">
                Ao confirmar, esta passa a ser a lista de quem aprova. A mudança vale para as aprovações
                daqui em diante — cotação que já está aguardando aprovação continua aguardando, e passa a
                depender de quem ficar na lista.
              </p>

              <div className="gestores-resumo">
                {adicionados.length > 0 && (
                  <div className="gestores-resumo-bloco">
                    <span className="fornecedor-info-label">Passam a aprovar ({adicionados.length})</span>
                    <span className="fornecedor-info-value">
                      {adicionados.map((g) => g.nome ?? g.userId).join(', ')}
                    </span>
                  </div>
                )}
                {removidos.length > 0 && (
                  <div className="gestores-resumo-bloco">
                    <span className="fornecedor-info-label">Deixam de aprovar ({removidos.length})</span>
                    <span className="fornecedor-info-value">
                      {removidos.map((g) => g.nome ?? g.userId).join(', ')}
                    </span>
                  </div>
                )}
                <div className="gestores-resumo-bloco">
                  <span className="fornecedor-info-label">Lista final</span>
                  <span className="fornecedor-info-value">
                    {selecionados.length === 0
                      ? 'nenhum gestor'
                      : `${selecionados.length} gestor(es): ${selecionados
                          .map((g) => g.nome ?? g.userId)
                          .join(', ')}`}
                  </span>
                </div>
              </div>

              {selecionados.length === 0 && (
                <p className="gestores-aviso-vazio">
                  Atenção: a lista final está vazia. Nenhum item acima do menor preço poderá ser aprovado, e
                  as cotações nessa situação ficam paradas até alguém ser cadastrado aqui.
                </p>
              )}
            </div>
          )}

          <div className="fornecedor-modal-acoes">
            {passo === 'edicao' ? (
              <>
                <button type="button" className="fornecedor-modal-btn" onClick={onFechar}>
                  Cancelar
                </button>
                <button
                  type="button"
                  className="fornecedor-modal-btn fornecedor-modal-btn--primario"
                  disabled={!temMudanca}
                  onClick={() => setPasso('confirmacao')}
                >
                  Salvar
                </button>
              </>
            ) : (
              <>
                <button
                  type="button"
                  className="fornecedor-modal-btn"
                  disabled={salvando}
                  onClick={() => setPasso('edicao')}
                >
                  Voltar
                </button>
                <button
                  type="button"
                  className="fornecedor-modal-btn fornecedor-modal-btn--primario"
                  disabled={salvando}
                  onClick={salvar}
                >
                  {salvando ? 'Salvando...' : 'Confirmar'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default GerenciarGestoresModal;
